import React, { useState, useEffect } from "react";
import axios from "axios";
import { FaCheckCircle, FaExclamationCircle } from "react-icons/fa";

const SelectProject = ({ onSelectProject }) => {
  const [projects, setProjects] = useState([]);
  const [budgetStatuses, setBudgetStatuses] = useState({});
  const email = localStorage.getItem("email");

  const fetchProjects = async () => {
    try {
      const response = await axios.post(
        "https://api.confidanto.com/projects-data/project-list",
        { email },
      );

      const updatedProjects = response.data.map((project) => {
        let customerId = "Not Connected";
        let platform = "Not Connected";

        // PER REQUIREMENT: strictly use the FIRST ad account in the array
        if (project.ad_accounts && project.ad_accounts.length > 0) {
          const firstAcc = project.ad_accounts[0];

          if (firstAcc) {
            customerId = firstAcc.customer_id;
            platform = firstAcc.platform;
          }
        }

        return {
          ...project,
          // Ensure we capture the ID regardless of whether the API returns 'id' or 'project_id'
          id: project.project_id || project.id,
          customerId,
          platform,
        };
      });

      setProjects(updatedProjects);
    } catch (error) {
      console.error("Error fetching projects:", error);
    }
  };

  useEffect(() => {
    fetchProjects();
  }, [email]);

  // Fetch Budget status for each loaded project sequentially
  useEffect(() => {
    if (projects.length === 0) return;

    const fetchBudgetAlerts = async () => {
      for (const project of projects) {
        if (project.customerId === "Not Connected") {
          setBudgetStatuses((prev) => ({ ...prev, [project.id]: false }));
          continue;
        }

        try {
          const reqBody = {
            project_id: parseInt(project.id, 10),
            customer_id: String(project.customerId), // Now uses the FIRST account's ID
            email: email,
          };

          const res = await axios.post(
            "https://api.confidanto.com/monthly-budget/check-budget-alert",
            reqBody,
          );

          const hasBudget = !res.data?.message?.includes("No budget");
          setBudgetStatuses((prev) => ({ ...prev, [project.id]: hasBudget }));
        } catch (error) {
          console.error(
            `Budget fetch failed for Project ${project.name} (ID: ${project.id}):`,
            error,
          );
          setBudgetStatuses((prev) => ({ ...prev, [project.id]: false }));
        }
      }
    };

    fetchBudgetAlerts();
  }, [projects, email]);

  const handleProjectClick = (project) => {
    // Saves exactly the info from the FIRST ad account
    localStorage.setItem("project_id", project.id);
    localStorage.setItem("customer_id", project.customerId);
    localStorage.setItem("project_name", project.name);

    // Also a good idea to save the platform of that first account for your tables/charts
    if (project.platform !== "Not Connected") {
      localStorage.setItem("platform", project.platform);
    }

    // Manually trigger event so Navbar updates the name instantly without refresh
    window.dispatchEvent(
      new CustomEvent("localStorageChange", {
        detail: { key: "project_name" },
      }),
    );

    onSelectProject(project.id);

    const newTab = window.open(`/project-details/${project.id}`, "_blank");
    if (newTab) {
      newTab.focus();
    } else {
      alert("Popup blocked! Please allow popups for this site.");
    }
  };

  return (
    <div
      className="w-full z-50 max-h-[340px] overflow-y-auto overflow-x-hidden pr-1 pb-1
                 [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:bg-transparent 
                 [&::-webkit-scrollbar-thumb]:bg-gray-300 [&::-webkit-scrollbar-thumb]:rounded-full"
    >
      <div className="flex flex-col gap-2">
        {projects.length === 0 ? (
          <p className="text-center text-gray-400 py-4 text-sm">
            No projects found.
          </p>
        ) : (
          projects.map((project) => (
            <div
              key={project.id}
              onClick={() => handleProjectClick(project)}
              className="group border border-gray-200 rounded-xl p-3 cursor-pointer bg-white transition-all duration-300 hover:border-indigo-500 hover:shadow-md"
            >
              {/* Always Visible Header */}
              <div className="flex justify-between items-start gap-2">
                <div className="overflow-hidden w-full">
                  <h3 className="font-semibold text-gray-800 text-sm sm:text-base truncate group-hover:text-indigo-600 transition-colors">
                    {project.name}
                  </h3>
                  <p className="text-xs text-gray-500 mt-0.5">
                    ID: {project.customerId}
                  </p>
                </div>

                {/* Budget Icon Status */}
                <div className="flex-shrink-0 mt-0.5 flex items-center justify-center cursor-help">
                  {budgetStatuses[project.id] === undefined ? (
                    // Loading skeleton for the icon
                    <div className="w-4 h-4 rounded-full border-2 border-gray-200 border-t-indigo-400 animate-spin" />
                  ) : budgetStatuses[project.id] ? (
                    <FaCheckCircle
                      className="text-green-600 text-base shadow-sm bg-white rounded-full"
                      title="Budget is active for this month"
                    />
                  ) : (
                    <FaExclamationCircle
                      className="text-orange-500 text-base shadow-sm bg-white rounded-full"
                      title="No budget configured for this month"
                    />
                  )}
                </div>
              </div>

              {/* Smooth Expanding Details (Columns) */}
              <div className="grid grid-rows-[0fr] group-hover:grid-rows-[1fr] transition-all duration-300 ease-in-out">
                <div className="overflow-hidden">
                  <div className="pt-2 mt-2 border-t border-gray-100 flex flex-col gap-2">
                    <div className="grid grid-cols-2 gap-x-4 gap-y-2 items-end text-xs">
                      <div className="flex flex-col">
                        <span className="font-semibold text-gray-400 uppercase text-[10px] tracking-wider mb-0.5">
                          Category
                        </span>
                        <span className="text-gray-700 truncate">
                          {project.category || "N/A"}
                        </span>
                      </div>

                      <div className="flex flex-col">
                        <span className="font-semibold text-gray-400 uppercase text-[10px] tracking-wider mb-0.5">
                          Client Type
                        </span>
                        <span className="text-gray-700 truncate">
                          {project.clientType || "N/A"}
                        </span>
                      </div>

                      <div className="flex flex-col overflow-hidden">
                        <span className="font-semibold text-gray-400 uppercase text-[10px] tracking-wider mb-0.5">
                          Website
                        </span>
                        {project.website ? (
                          <a
                            href={
                              project.website.startsWith("http")
                                ? project.website
                                : `https://${project.website}`
                            }
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="text-indigo-500 hover:text-indigo-700 hover:underline truncate block"
                          >
                            {project.website}
                          </a>
                        ) : (
                          <span className="text-gray-700 truncate block">
                            N/A
                          </span>
                        )}
                      </div>

                      <div className="flex justify-end items-center text-indigo-600 font-medium gap-1 text-xs mb-[2px]">
                        Go to Project
                        <svg
                          className="w-3.5 h-3.5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M14 5l7 7m0 0l-7 7m7-7H3"
                          />
                        </svg>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default SelectProject;
